/*
 * ToolRegistry — the single source of truth for every agent-callable capability.
 *
 * Architecture (see docs/webmcp.md):
 *
 *   WebMCP (browser)  ─┐
 *   Debug panel       ─┼─> ToolRegistry.invoke() ─> tool handler ─> app commands
 *   Playwright tests  ─┘
 *
 * Every transport goes through this one object, so a tool tested from the debug
 * panel or from Playwright is provably the same code an agent reaches. The
 * registry itself knows nothing about WebMCP, MCP, or the editor.
 */

import { ErrorCode, ToolError, toErrorPayload } from './errors.js'
import { assertSupportedSchema, validate, applyDefaults } from './validate.js'

const NAME_PATTERN = /^[a-z][a-z0-9_]{2,63}$/

/**
 * @typedef {object} ToolDefinition
 * @property {string} name          stable snake_case identifier, e.g. "get_app_status"
 * @property {string} title         short human label for UIs
 * @property {string} description   what the tool does, written for an agent
 * @property {object} inputSchema   JSON Schema (object type) describing the arguments
 * @property {object} [outputSchema] JSON Schema describing the structured result
 * @property {boolean} [readOnly]   true when the tool provably does not mutate state
 * @property {(args: object, ctx: object) => (object|Promise<object>)} handler
 */

export class ToolRegistry {
  constructor ({ maxLogEntries = 50 } = {}) {
    /** @type {Map<string, ToolDefinition>} */
    this._tools = new Map()
    this._listeners = new Set()
    this._log = []
    this._maxLogEntries = maxLogEntries
    this._sequence = 0
  }

  /**
   * @param {ToolDefinition} definition
   * @returns {() => void} an unregister function
   */
  register (definition) {
    const { name, title, description, inputSchema, outputSchema, readOnly = false, handler } = definition || {}

    if (!NAME_PATTERN.test(name || '')) {
      throw new Error(`Invalid tool name ${JSON.stringify(name)} — expected snake_case matching ${NAME_PATTERN}`)
    }
    if (this._tools.has(name)) {
      throw new Error(`Tool "${name}" is already registered`)
    }
    if (typeof description !== 'string' || description.trim().length < 10) {
      throw new Error(`Tool "${name}" needs a description an agent can act on`)
    }
    if (typeof handler !== 'function') {
      throw new Error(`Tool "${name}" needs a handler function`)
    }
    if (!inputSchema || inputSchema.type !== 'object') {
      throw new Error(`Tool "${name}" needs an object inputSchema (use { type: 'object', properties: {} } for no arguments)`)
    }
    assertSupportedSchema(inputSchema)
    if (outputSchema) assertSupportedSchema(outputSchema)

    this._tools.set(name, {
      name,
      title: title || name,
      description,
      inputSchema,
      outputSchema,
      readOnly,
      handler
    })
    this._emit()
    return () => this.unregister(name)
  }

  unregister (name) {
    const existed = this._tools.delete(name)
    if (existed) this._emit()
    return existed
  }

  has (name) { return this._tools.has(name) }

  get (name) { return this._tools.get(name) }

  /** Tool descriptors without handlers — safe to serialize into a UI or a transport. */
  list () {
    return [...this._tools.values()].map(({ handler, ...rest }) => rest)
  }

  /**
   * Invoke a tool. Never throws: every outcome is a structured envelope so that
   * transports (and agents) get the same shape for success and failure.
   *
   * @param {string} name
   * @param {object} [args]
   * @param {{source?: string}} [options] where the call came from ('webmcp' | 'debug-panel' | 'test')
   * @returns {Promise<{ok: true, result: object} | {ok: false, error: {code: string, message: string, details?: object}}>}
   */
  async invoke (name, args = {}, { source = 'unknown' } = {}) {
    const startedAt = Date.now()
    const entry = {
      id: ++this._sequence,
      tool: name,
      args,
      source,
      timestamp: new Date(startedAt).toISOString(),
      durationMs: 0,
      ok: false,
      result: undefined,
      error: undefined
    }

    try {
      const tool = this._tools.get(name)
      if (!tool) {
        throw new ToolError(
          ErrorCode.TOOL_NOT_FOUND,
          `Unknown tool "${name}"`,
          { availableTools: [...this._tools.keys()] }
        )
      }

      if (args !== undefined && args !== null && (typeof args !== 'object' || Array.isArray(args))) {
        throw new ToolError(ErrorCode.INVALID_ARGUMENTS, 'Arguments must be a JSON object', { received: typeof args })
      }

      const withDefaults = applyDefaults(tool.inputSchema, args || {})
      const { valid, errors } = validate(tool.inputSchema, withDefaults)
      if (!valid) {
        throw new ToolError(
          ErrorCode.INVALID_ARGUMENTS,
          `Invalid arguments for "${name}": ` + errors.map((e) => `${e.path} ${e.message}`).join('; '),
          { violations: errors }
        )
      }

      const result = await tool.handler(withDefaults, { source, registry: this })
      entry.ok = true
      entry.result = result
      return { ok: true, result }
    } catch (err) {
      entry.error = toErrorPayload(err)
      return { ok: false, error: entry.error }
    } finally {
      entry.durationMs = Date.now() - startedAt
      this._record(entry)
    }
  }

  /** Most recent invocations, newest first. Used by the debug panel. */
  invocations () { return [...this._log] }

  lastInvocation () { return this._log[0] }

  clearInvocations () {
    this._log = []
    this._emit()
  }

  /** Subscribe to registry changes (tool set or invocation log). */
  subscribe (listener) {
    this._listeners.add(listener)
    return () => this._listeners.delete(listener)
  }

  _record (entry) {
    this._log.unshift(entry)
    if (this._log.length > this._maxLogEntries) this._log.length = this._maxLogEntries
    this._emit()
  }

  _emit () {
    for (const listener of this._listeners) {
      try { listener(this) } catch (e) { console.error('[webmcp] registry listener failed', e) }
    }
  }
}

/** The application-wide registry instance. */
export const toolRegistry = new ToolRegistry()
